import type {
  FacetHit,
  MultipleQueriesQuery,
  MultipleQueriesResponse,
  SearchForFacetValuesQueryParams,
  SearchForFacetValuesResponse,
  SearchOptions,
} from '@algolia/client-search';
import type {
  ApolloClient,
  FetchPolicy,
  TypedDocumentNode,
} from '@apollo/client';
import { gql, useApolloClient } from '@apollo/client';
import type { SearchClient } from 'algoliasearch-helper/types/algoliasearch';
import { useMemo } from 'react';

import type {
  Rarity,
  SearchFacetDefinitionInput,
  SearchRefinementInput,
  SearchSortInput,
} from '@sorare/core/src/__generated__/globalTypes';
import {
  SearchSortDirection,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import type { GallerySortName } from '@sorare/core/src/contexts/config';
import { SorareSearchClient } from '@sorare/core/src/hooks/search/useSearchClient';

import type {
  extractQueryGeneric,
  extractVariableGeneric,
} from '#src/lib/searchClient.ts';

import type {
  useCardsSearchClient_searchCardFacetValues,
  useCardsSearchClient_searchCards,
} from './__generated__/useCardsSearchClient.graphql.ts';

type GallerySortFields = Record<
  GallerySortName,
  {
    field: string | Record<Sport, string>;
    direction: SearchSortDirection;
  }
>;
export const MAPPED_SORTS: GallerySortFields = {
  'Cards Highest Average Score': {
    field: {
      [Sport.FOOTBALL]: 'so5.last_ten_played_so5_average_score',
      [Sport.BASEBALL]: 'so5.last_ten_played_so5_average_score',
      [Sport.NBA]: 'so5.last_ten_played_so5_average_score',
    },
    direction: SearchSortDirection.DESC,
  },
  'Cards Lowest Average Score': {
    field: {
      [Sport.FOOTBALL]: 'so5.last_ten_played_so5_average_score',
      [Sport.BASEBALL]: 'so5.last_ten_played_so5_average_score',
      [Sport.NBA]: 'so5.last_ten_played_so5_average_score',
    },
    direction: SearchSortDirection.ASC,
  },
  'Cards Player Name': {
    field: 'player.display_name',
    direction: SearchSortDirection.ASC,
  },
  'Cards Highest Price': {
    field: 'price',
    direction: SearchSortDirection.DESC,
  },
  'Cards Lowest Price': {
    field: 'price',
    direction: SearchSortDirection.ASC,
  },
  'Sealing Value': {
    field: 'sealing_value',
    direction: SearchSortDirection.DESC,
  },
  'Cards New': {
    field: 'user_owner.from',
    direction: SearchSortDirection.DESC,
  },
  Tier: {
    field: 'gameplay_tier_index',
    direction: SearchSortDirection.ASC,
  },
  'Tier Desc': {
    field: 'gameplay_tier_index',
    direction: SearchSortDirection.DESC,
  },
  'Special editions': {
    field: 'card_edition_sort_score',
    direction: SearchSortDirection.DESC,
  },
};

type BaseQueryVariables = {
  query: string;
  page: number;
  pageSize: number;
  sorts: SearchSortInput[];
  rarity: Rarity | undefined;
  sport: Sport;
  duplicatedKey?: string;
  advancedFilters?: string | null;
  refinements: SearchRefinementInput[];
  facets: SearchFacetDefinitionInput[];
  onlyFollowed?: boolean;
  userSlug: string;
  cardSetSlug?: string | null;
};

type BaseQuery<Variables extends BaseQueryVariables> = TypedDocumentNode<
  {
    user: {
      slug: string;
      searchCards: useCardsSearchClient_searchCards;
    };
  },
  Variables
>;

type BaseFacetValuesQueryVariables = {
  query: string;
  field: string;
  rarity: Rarity | undefined;
  sport: Sport;
  duplicatedKey?: string;
  facetQuery: string;
  advancedFilters?: string | null;
  refinements: SearchRefinementInput[];
  userSlug: string;
  cardSetSlug?: string | null;
};

type BaseFacetValuesQuery<Variables extends BaseFacetValuesQueryVariables> =
  TypedDocumentNode<
    {
      user: {
        slug: string;
        searchCardFacetValues: useCardsSearchClient_searchCardFacetValues[];
      };
    },
    Variables
  >;

class CardsSearchClient<
  TQuery extends BaseQuery<TVariables>,
  TFacetValuesQuery extends BaseFacetValuesQuery<TFacetValuesVariables>,
  TVariables extends BaseQueryVariables = extractVariableGeneric<TQuery>,
  TFacetValuesVariables extends BaseFacetValuesQueryVariables =
    extractVariableGeneric<TFacetValuesQuery>,
>
  extends SorareSearchClient
  implements SearchClient
{
  private client: ApolloClient<object>;

  private query: TQuery;

  private facetValuesQuery: TFacetValuesQuery;

  private options: {
    userSlug: string;
    variables?: Record<string, unknown>;
    sport: Sport;
    rarity: Rarity | undefined;
    duplicatedKey?: string;
    cardSetSlug?: string | null;
    fetchPolicy?: FetchPolicy;
  };

  constructor(
    client: ApolloClient<object>,
    query: TQuery,
    facetValuesQuery: TFacetValuesQuery,
    options: {
      userSlug: string;
      sport: Sport;
      rarity: Rarity | undefined;
      duplicatedKey?: string;
      cardSetSlug?: string | null;
      variables?: Record<string, unknown>;
    }
  ) {
    super();
    this.client = client;
    this.query = query;
    this.facetValuesQuery = facetValuesQuery;
    this.options = options;
  }

  private toSorts(indexName: string, sport: Sport): SearchSortInput[] {
    if (indexName in MAPPED_SORTS) {
      const s = MAPPED_SORTS[indexName as GallerySortName];
      if (typeof s.field === 'string') {
        return [{ field: s.field, direction: s.direction }];
      }
      return [{ field: s.field[sport], direction: s.direction }];
    }
    return [];
  }

  async search<THit>(queries: readonly MultipleQueriesQuery[]) {
    // only the main query is important, all others are for the disjunctive facets and are handled natively
    const query = queries[0];
    const params = query.params!;
    const { data } = await this.client.query<
      extractQueryGeneric<TQuery>,
      TVariables
    >({
      query: this.query,
      fetchPolicy: this.options.fetchPolicy,
      variables: {
        userSlug: this.options.userSlug,
        rarity: this.options.rarity,
        sport: this.options.sport,
        duplicatedKey: this.options.duplicatedKey,
        cardSetSlug: this.options.cardSetSlug,
        query: params.query || '',
        page: (params.page || 0) + 1, // 1-based
        pageSize: params.hitsPerPage || 1,
        sorts: this.toSorts(query.indexName, this.options.sport),
        advancedFilters: params.filters,
        refinements: this.toRefinements(
          params.facetFilters as string[][],
          params.numericFilters as string[] | undefined
        ),
        facets: this.toFacets(params.facets),
        ...this.options.variables,
      } as TVariables,
    });

    const res = data.user.searchCards;

    const response: MultipleQueriesResponse<THit> = {
      results: [
        {
          hits: res.hits.map(hit => ({
            ...(hit as THit),
            slug: hit.slug,
            objectID: hit.slug,
          })),
          facets: Object.fromEntries(
            res.facets.map(facet => [
              facet.field,
              Object.fromEntries(facet.values.map(v => [v.value, v.count])),
            ])
          ),
          facets_stats: Object.fromEntries(
            res.facets.map(facet => [
              facet.field,
              {
                min: parseInt(facet.min?.value || '0', 10),
                max: parseInt(facet.max?.value || '0', 10),
                avg: 0,
                sum: 0,
              },
            ])
          ),
          page: res.page,
          nbPages: res.nbPages,
          nbHits: res.nbHits,
          hitsPerPage: params.hitsPerPage!,
          processingTimeMS: 0,
          query: params.query!,
          exhaustiveNbHits: true,
          params: '',
          index: query.indexName,
        },
      ],
    };

    return response;
  }

  async searchForFacetValues(
    queries: ReadonlyArray<{
      readonly indexName: string;
      readonly params: SearchForFacetValuesQueryParams & SearchOptions;
    }>
  ) {
    // only the first query is handled - which should cover all use-cases
    const query = queries[0];
    const params = query.params!;

    const { data } = await this.client.query<
      extractQueryGeneric<TFacetValuesQuery>,
      TFacetValuesVariables
    >({
      query: this.facetValuesQuery,
      variables: {
        userSlug: this.options.userSlug,
        rarity: this.options.rarity,
        sport: this.options.sport,
        duplicatedKey: this.options.duplicatedKey,
        cardSetSlug: this.options.cardSetSlug,
        query: params.query || '',
        field: params.facetName,
        facetQuery: params.facetQuery || '',
        advancedFilters: params.filters,
        refinements: this.toRefinements(
          params.facetFilters as string[][],
          params.numericFilters as string[]
        ),
      } as TFacetValuesVariables,
    });

    const facetHits: FacetHit[] = data.user.searchCardFacetValues.map(v => ({
      value: v.value,
      count: v.count,
      highlighted: '',
    }));

    const response: SearchForFacetValuesResponse[] = [
      {
        facetHits,
        exhaustiveFacetsCount: true,
      },
    ];
    return response;
  }
}

export const useCardsSearchClient = <
  TQuery extends BaseQuery<TVariables>,
  TFacetValuesQuery extends BaseFacetValuesQuery<TFacetValuesVariables>,
  TVariables extends BaseQueryVariables = extractVariableGeneric<TQuery>,
  TFacetValuesVariables extends BaseFacetValuesQueryVariables =
    extractVariableGeneric<TFacetValuesQuery>,
>(
  query: TQuery,
  facetValuesQuery: TFacetValuesQuery,
  options: {
    userSlug: string;
    rarity: Rarity | undefined;
    sport: Sport;
    duplicatedKey?: string;
    cardSetSlug?: string | null;
    variables?: Record<string, unknown>;
    fetchPolicy?: FetchPolicy;
  }
): SearchClient => {
  const apolloClient = useApolloClient();
  const optionsString = JSON.stringify(options);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const searchClient: SearchClient = useMemo(() => {
    return new CardsSearchClient<TQuery, TFacetValuesQuery>(
      apolloClient,
      query,
      facetValuesQuery,
      options
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [apolloClient, query, facetValuesQuery, optionsString]);

  return searchClient;
};

useCardsSearchClient.fragments = {
  searchCardFacetValues: gql`
    fragment useCardsSearchClient_searchCardFacetValues on SearchFacetValue {
      value
      count
    }
  ` as TypedDocumentNode<useCardsSearchClient_searchCardFacetValues>,
  // oxlint-disable-next-line sorare/no-unused-fragment-fields
  searchCards: gql`
    fragment useCardsSearchClient_searchCards on SearchCards {
      page
      nbPages
      nbHits
      hits {
        slug
        anyPlayer {
          slug
        }
      }
      facets {
        field
        values {
          value
          count
        }
        min {
          value
        }
        max {
          value
        }
      }
      processingTimeMs {
        facetValuesFts
        faceting
        filtering
        fts
        sorting
        total
      }
    }
  ` as TypedDocumentNode<useCardsSearchClient_searchCards>,
};
